/**
 * React port of earthDemo.js (reference only — this repo is not a React app).
 *
 * Setup differences from vanilla:
 * - Install: npm install three react react-dom
 * - Import three from npm (not the index.html import map "three" / "jsm/" paths)
 * - Put texture files in public/images/ so paths like "/images/earth-map-5k.jpg" resolve
 *
 * Minimal Vite usage:
 *   npm create vite@latest my-earth-app -- --template react
 *   cd my-earth-app && npm install && npm install three
 *   cp -r /path/to/threejs-demo/images public/images
 *   copy EarthDemo.jsx into src/EarthDemo.jsx
 *   // App.jsx: import EarthDemo from "./EarthDemo"; export default () => <EarthDemo />;
 */

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import earthMap from "../../images/earth/earth-map-5k.jpg";
import earthNightmap from "../../images/earth/earth-nightmap-4k.jpg";
import earthClouds from "../../images/earth/earth-clouds-4k.jpg";
// import StarField from "./helpers/getStarField.js";

function disposeMaterial(material) {
  if (Array.isArray(material)) {
    material.forEach(disposeMaterial);
    return;
  }
  if (material.map) material.map.dispose();
  material.dispose();
}

export default function EarthDemo() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getSize = () => ({
      w: container.clientWidth || window.innerWidth,
      h: container.clientHeight || window.innerHeight,
    });

    const { w, h } = getSize();
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, w / h, 0.1, 10);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    container.appendChild(renderer.domElement);

    const earthGroup = new THREE.Group();
    earthGroup.rotation.z = (-23.4 * Math.PI) / 180;
    scene.add(earthGroup);

    const controls = new OrbitControls(camera, renderer.domElement);

    const loader = new THREE.TextureLoader();
    const geometry = new THREE.IcosahedronGeometry(1, 12);

    const material = new THREE.MeshStandardMaterial({
      map: loader.load(earthMap),
      // map: loader.load("/images/jupitermap.jpg"),
      // flatShading: true,
    });
    const earthMesh = new THREE.Mesh(geometry, material);
    earthGroup.add(earthMesh);

    const lightsMat = new THREE.MeshBasicMaterial({
      map: loader.load(earthNightmap),
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.5,
    });
    const lightsMesh = new THREE.Mesh(geometry, lightsMat);
    earthGroup.add(lightsMesh);

    const cloudsMat = new THREE.MeshStandardMaterial({
      map: loader.load(earthClouds),
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.9,
    });
    const cloudsMesh = new THREE.Mesh(geometry, cloudsMat);
    cloudsMesh.scale.setScalar(1.01);
    earthGroup.add(cloudsMesh);

    // const stars = StarField();
    // scene.add(stars);

    const sunLight = new THREE.DirectionalLight(0xffffff);
    sunLight.position.set(-2, 0.5, 1.5);
    scene.add(sunLight);

    let frameId;
    function animate() {
      frameId = requestAnimationFrame(animate);
      earthMesh.rotation.y += 0.002;
      lightsMesh.rotation.y += 0.002;
      cloudsMesh.rotation.y += 0.0024;
      renderer.render(scene, camera);
    }
    animate();

    const onResize = () => {
      const { w: newW, h: newH } = getSize();
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
      renderer.setSize(newW, newH);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      controls.dispose();
      geometry.dispose();
      disposeMaterial(material);
      disposeMaterial(lightsMat);
      disposeMaterial(cloudsMat);
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={containerRef} className="earth-demo" />;
}
